import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";

import { useCallback, useEffect, useState } from "react";

import { ContactsList } from "models/contactsList";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useToggle } from "usehooks-ts";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faListUl } from "@fortawesome/free-solid-svg-icons/faListUl";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faUserTie } from "@fortawesome/free-solid-svg-icons/faUserTie";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useAuthentication from "hooks/useAuthentication";
import { emberAppBaseUrl } from "utils/envVariables";
import { USE_EMBER_IFRAME } from "consts";

const AddToWatchlistButton = dynamic(
  () => import("components/AddToWatchlistButton"),
  { ssr: false }
);

const DiscussionEditor = dynamic(() => import("components/DiscussionEditor"), {
  ssr: false,
});

const EditListModal = dynamic(
  () => import("components/ContactsListsPage/EditListModal"),
  { ssr: false }
);

const ImportContactsModal = dynamic(
  () => import("components/ContactsListsPage/ImportContactsModal"),
  { ssr: false }
);

type EmptyStateProps = {
  type:
    | "search"
    | "publication"
    | "filing"
    | "presentation"
    | "event"
    | "news"
    | "discussion"
    | "data"
    | "watchlist-update"
    | "entity-peers-update"
    | "topic"
    | "contacts-list"
    | "earnings-alert"
    | "top-investor-questions"
    | "contacts-lists"
    | "institution";
  subType?: string;
  query?: string;
  onActionClick?: () => void;
  onClearClick?: () => void;
  onDiscussionPost?: () => void;
  onCreateNewSuccess?: (_item: any) => void;
  entity?: any;
  contactsList?: ContactsList;
  isFocused?: boolean;
  onFocusedToggle?: () => void;
  className?: string;
  showComposer?: boolean;
};

export default function EmptyState({
  type,
  subType,
  query,
  onActionClick,
  onClearClick: _onClearClick,
  onDiscussionPost,
  showComposer = true,
  onCreateNewSuccess,
  entity,
  contactsList,
  isFocused,
  onFocusedToggle,
  className,
}: EmptyStateProps) {
  const { account: loggedInAccount } = useAuthentication();
  const [hoverTopInvestorQuestionsButton, setHoverTopInvestorQuestionsButton] =
    useState(false);

  const [showCreateNewModal, toggleCreateNewModal] = useToggle();

  const [shouldHideAsk] = useLocalStorage(`shouldHideAsk`, true);

  const onMessage = useCallback(
    (event) => {
      const messageType = event?.data[0];
      switch (messageType) {
        case "discussion-compose-success-post": {
          onDiscussionPost && onDiscussionPost();
          return;
        }
        case "discussion-compose-on-post": {
          return;
        }
      }
    },
    [onDiscussionPost]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  if (type === "search") {
    return (
      <div className={twMerge("p-[40px] text-intelligent-silver", className)}>
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px] font-semibold"
          )}
        >
          No matches found for “{query}”
        </div>
        <div className={twMerge("text-[14px]", "mt-[10px]")}>
          Your search - “{query}” - did not match any results.
        </div>
        <div className="">
          <p>Suggestions</p>
          <ul>
            {shouldHideAsk && <li>Enable AI mode to see more results</li>}
            <li>Make sure that all words are spelled correctly.</li>
            <li>Try other keywords</li>
            <li>Try more general keywords.</li>
            <li>Try fewer keywords.</li>
          </ul>
        </div>
      </div>
    );
  }

  if (type === "publication") {
    return (
      <div
        className={twMerge(
          "text-center pt-[182px] pb-[170px] mx-auto max-w-[250px] md:max-w-none",
          className
        )}
      >
        <Image
          src={require("./empty-state-publications.svg")}
          alt="Publication Icon"
          height={92}
          width={90}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No publications yet
        </div>
        {entity && (
          <>
            {subType ? (
              <>
                <div
                  className={twMerge(
                    "text-[14px] text-shady-gray",
                    "mt-[20px]"
                  )}
                >
                  {subType === "insights" &&
                    "There aren’t any Insights right now"}
                  {subType === "broker-reports" &&
                    "There aren’t any broker reports right now"}
                  {subType === "sponsored" &&
                    "There aren’t any sponsored reports right now"}
                  {subType === "syndicated" &&
                    "There aren’t any syndicated reports right now"}
                  {subType === "podcast" &&
                    "There aren’t any podcasts right now"}
                  {subType === "buyside" &&
                    "There aren’t any buy-side reports right now"}
                  {subType === "primer" &&
                    "No AI primers are available right now. Create one using Smartkarma AI."}
                </div>
                <div
                  className={twMerge(
                    "text-[16px]",
                    "text-smart-teal",
                    "cursor-pointer",
                    "mt-[40px]"
                  )}
                  onClick={onActionClick}
                >
                  Back to &quot;All&quot;
                </div>
              </>
            ) : (
              <>
                <div
                  className={twMerge(
                    "text-[14px] text-shady-gray",
                    "mt-[20px]"
                  )}
                >
                  Add {entity?.prettyName} to your Watchlist to get alerts on
                  new publications
                </div>
                <AddToWatchlistButton entity={entity}>
                  {({ addToWatchlist, isWatching }) => (
                    <div
                      className={twMerge(
                        "text-[16px]",
                        isWatching ? "text-jade" : "text-smart-teal",
                        "cursor-pointer",
                        "mt-[40px]"
                      )}
                      onClick={addToWatchlist}
                    >
                      <FontAwesomeIcon icon={isWatching ? faCheck : faPlus} />{" "}
                      {isWatching ? "Added" : "Add"} to Watchlist
                    </div>
                  )}
                </AddToWatchlistButton>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  if (type === "filing") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-filings.svg")}
          alt="Filing Icon"
          height={92}
          width={89}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No filings found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display filings we find for {entity.prettyName} here
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "earnings-alert") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-earnings.png")}
          alt="Filing Icon"
          height={92}
          className="opacity-30"
          width={89}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No alerts found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display earnings and operating data alerts we find for{" "}
              {entity.prettyName} here
            </div>
            <AddToWatchlistButton entity={entity}>
              {({ addToWatchlist, isWatching }) => (
                <div
                  className={twMerge(
                    "text-[16px]",
                    isWatching ? "text-jade" : "text-smart-teal",
                    "cursor-pointer",
                    "mt-[40px]"
                  )}
                  onClick={addToWatchlist}
                >
                  <FontAwesomeIcon icon={isWatching ? faCheck : faPlus} />{" "}
                  {isWatching ? "Added" : "Add"} to Watchlist
                </div>
              )}
            </AddToWatchlistButton>
          </>
        )}
      </div>
    );
  }

  if (type === "top-investor-questions") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-publications.svg")}
          alt="Meeting Prep Icon"
          height={92}
          width={90}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No Meeting Prep yet
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              Generate Meeting Prep for {entity.prettyName} to get
              management-ready talking points and probing questions.
            </div>
            <div
              className={twMerge(
                "mx-auto mt-[30px] w-fit",
                "flex items-center justify-center gap-[5px]",
                "rounded-full border border-solid border-ink-pink",
                "bg-white px-[16px] py-[7px] text-[12px] text-ink-pink",
                "hover:bg-ink-pink hover:text-white",
                onActionClick
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-60"
              )}
              onClick={onActionClick}
              onMouseEnter={() => setHoverTopInvestorQuestionsButton(true)}
              onMouseLeave={() => setHoverTopInvestorQuestionsButton(false)}
            >
              <Image
                src={
                  hoverTopInvestorQuestionsButton
                    ? "https://assets.smartkarma.com/icons/sparkle-single/sparkle-white.svg"
                    : "https://assets.smartkarma.com/icons/sparkle-single/sparkle-pink.svg"
                }
                alt="Sparkle"
                width={20}
                height={15}
              />
              Generate
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "presentation") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-presentations.svg")}
          alt="Presentation Icon"
          height={92}
          width={89}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No presentations found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display presentations we find for {entity.prettyName} here
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "event") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[198px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-events.svg")}
          alt="Event Icon"
          height={85}
          width={95}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No events found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display events we find for {entity.prettyName} here
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "news") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-news.svg")}
          alt="News Icon"
          height={92}
          width={86}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No news found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display news we find for {entity.prettyName} here
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "discussion") {
    const params = [];

    if (entity) {
      params.push(`entity-id=${entity.id}`);
    }

    const iframeSrc = `${emberAppBaseUrl}/embed/discussion-compose?${params.join(
      "&"
    )}`;

    return (
      <div
        className={twMerge(
          "mx-auto max-w-[250px] md:max-w-none text-center pt-[172px] pb-[170px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-discussions.svg")}
          alt="Discussion Icon"
          height={92}
          width={125}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[46px]"
          )}
        >
          No discussions yet
        </div>
        {loggedInAccount?.canPostComment ? (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              Ask a question or post a comment to get the conversation started
            </div>
            {loggedInAccount?.isInternalUser || !USE_EMBER_IFRAME ? (
              showComposer ? (
                <div className="text-left mt-[20px]">
                  <DiscussionEditor
                    style="v3"
                    commentable={entity}
                    afterSubmit={() => onDiscussionPost && onDiscussionPost()}
                  />
                </div>
              ) : null
            ) : (
              <iframe
                className={twMerge(
                  "w-full h-[400px] overflow-hidden mt-[45px]"
                )}
                src={iframeSrc}
              />
            )}
          </>
        ) : (
          entity && (
            <>
              <div
                className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
              >
                Add {entity.prettyName} to your Watchlist to get alerts when
                someone starts a discussion
              </div>
              <AddToWatchlistButton entity={entity}>
                {({ addToWatchlist, isWatching }) => (
                  <div
                    className={twMerge(
                      "text-[16px]",
                      isWatching ? "text-jade" : "text-smart-teal",
                      "cursor-pointer",
                      "mt-[40px]"
                    )}
                    onClick={addToWatchlist}
                  >
                    <FontAwesomeIcon icon={isWatching ? faCheck : faPlus} />{" "}
                    {isWatching ? "Added" : "Add"} to Watchlist
                  </div>
                )}
              </AddToWatchlistButton>
            </>
          )
        )}
      </div>
    );
  }

  if (type === "data") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-data.svg")}
          alt="Data Icon"
          height={92}
          width={86}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No data found
        </div>
        {entity && (
          <>
            <div
              className={twMerge("text-[14px] text-shady-gray", "mt-[20px]")}
            >
              We’ll display financial data we find for {entity.prettyName} here
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "watchlist-update") {
    return (
      <div
        className={twMerge(
          " mx-auto max-w-[250px] md:max-w-none text-center pt-[182px] pb-[170px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-publications.svg")}
          alt="Publication Icon"
          height={92}
          width={90}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No recent updates
        </div>

        <div
          className={twMerge("text-[14px] text-shady-gray", "block mt-[48px]")}
        >
          Add more entities to your watchlist or extend the time horizon to view
          more updates.
        </div>
      </div>
    );
  }

  if (type === "entity-peers-update") {
    return (
      <div
        className={twMerge(
          " mx-auto max-w-[250px] md:max-w-none text-center pt-[182px] pb-[170px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-publications.svg")}
          alt="Publication Icon"
          height={92}
          width={90}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No recent updates
        </div>

        <div
          className={twMerge("text-[14px] text-shady-gray", "block mt-[48px]")}
        >
          Add more entities to your peers or extend the time horizon to view
          more updates.
        </div>
      </div>
    );
  }

  if (type === "topic") {
    return (
      <div
        className={twMerge(
          " mx-auto max-w-[250px] md:max-w-none text-center pt-[182px] pb-[170px]",
          className
        )}
      >
        <Image
          src={require("./empty-state-publications.svg")}
          alt="Publication Icon"
          height={92}
          width={90}
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No Themes Yet
        </div>
        <div
          className={twMerge("text-[14px] text-shady-gray", "block mt-[20px]")}
        >
          {isFocused ? (
            <>
              <span
                className="text-smart-teal cursor-pointer"
                onClick={() => onFocusedToggle?.()}
              >
                Switch off
              </span>{" "}
              the Focused Themes Toggle or
              <br />
              Search for another {subType || "Entity"}
            </>
          ) : (
            `Try searching for another ${subType || "Entity"}`
          )}
        </div>
      </div>
    );
  }

  if (type === "contacts-list") {
    return (
      <>
        <div
          className={twMerge(
            " mx-auto max-w-[250px] md:max-w-none text-center md:pt-[182px] md:pb-[170px]",
            className
          )}
        >
          <FontAwesomeIcon
            icon={faListUl}
            className="text-[50px] md:text-[90px] text-shady-gray"
          />
          <div
            className={twMerge(
              "text-[22px] text-intelligent-silver",
              "mt-[36px]"
            )}
          >
            No Contacts Yet
          </div>
          <div
            className={twMerge(
              "text-[14px] text-shady-gray",
              "block mt-[20px]"
            )}
          >
            <p>
              Add Investors to your Contact List and reach out to them with your
              company updates.
            </p>
            <p>
              <span
                className="text-smart-teal cursor-pointer"
                onClick={toggleCreateNewModal}
              >
                Bulk Upload from a CSV file
              </span>
              , or{" "}
              <Link href="/targeting" className="text-smart-teal">
                Target new Investors »
              </Link>
            </p>
          </div>
        </div>
        {showCreateNewModal && contactsList && (
          <ImportContactsModal
            contactsList={contactsList}
            onClose={toggleCreateNewModal}
          />
        )}
      </>
    );
  }

  if (type === "contacts-lists") {
    return (
      <>
        <div
          className={twMerge(
            " mx-auto max-w-[250px] md:max-w-none text-center md:pt-[182px] md:pb-[170px]",
            className
          )}
        >
          <FontAwesomeIcon
            icon={faListUl}
            className="text-[50px] md:text-[90px] text-shady-gray"
          />
          <div
            className={twMerge(
              "text-[22px] text-intelligent-silver",
              "mt-[36px]"
            )}
          >
            No Contact List Yet
          </div>
          <div
            className={twMerge(
              "text-[14px] text-shady-gray",
              "block mt-[20px]"
            )}
          >
            <span
              className="text-smart-teal cursor-pointer"
              onClick={toggleCreateNewModal}
            >
              Create a new Contact List
            </span>
          </div>
        </div>
        {showCreateNewModal && (
          <EditListModal
            onClose={toggleCreateNewModal}
            onSuccess={onCreateNewSuccess}
          />
        )}
      </>
    );
  }

  if (type === "institution") {
    return (
      <div
        className={twMerge(
          "text-center mx-auto max-w-[250px] md:max-w-none pt-[60px] md:pt-[191px] pb-[300px] md:pb-[220px]",
          className
        )}
      >
        <FontAwesomeIcon
          icon={faUserTie}
          className="text-[50px] md:text-[90px] text-shady-gray"
        />
        <div
          className={twMerge(
            "text-[22px] text-intelligent-silver",
            "mt-[36px]"
          )}
        >
          No Investors Found
        </div>
        {query && (
          <div
            className={twMerge(
              "text-[14px] text-shady-gray",
              "block mt-[20px]"
            )}
          >
            {`We couldn’t find any investors matching your filters. Try adjusting your search criteria`}
          </div>
        )}
      </div>
    );
  }

  return <></>;
}
